// WHAT: Withings Settings View Component
// WHY:  Displays Withings integration settings with API config and norm ranges
// DOMAIN: Settings
// DEPENDENCIES: UI components, WithingsApiConfig, WithingsNormRanges components, Lucide icons
// RISKS:
//   - NOTE: Component is a simple wrapper (minimal logic)

import { Scale } from "lucide-react";

import { Card, Stack, Heading } from "@/components/ui";

import { WithingsApiConfig, WithingsNormRanges } from "./withingssettings";

/**
 * WHAT:
 *   Settings view component for Withings health integration.
 *
 * WHY:
 *   Provides interface for configuring Withings API credentials and normal ranges.
 *
 * BEHAVIOR:
 *   - Displays header section with scale icon and description
 *   - Renders WithingsApiConfig component for API credentials
 *   - Renders WithingsNormRanges component for health metric ranges
 *   - Uses TracQ branding colors and styling
 *
 * RISKS:
 *   - NOTE: Component is a simple wrapper (minimal logic)
 *   - NOTE: No error handling or loading states
 *
 * @returns {JSX.Element} Withings settings view component
 *
 */
export function WithingsSettingsView() {
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <Scale className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              Withings Integration
            </Heading>
            <p className="text-slate-300">
              Configure your Withings API credentials for scale data integration and health metrics
              tracking.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* API Configuration Component */}
      <WithingsApiConfig />

      {/* Normal Range Settings Component */}
      <WithingsNormRanges />
    </div>
  );
}
